import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon, People as PeopleIcon, Add as AddIcon,
  History as HistoryIcon, SwapHoriz as AssignIcon,
} from '@mui/icons-material';

import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PatientList() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [clinics, setClinics] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ full_name: '', email: '', password: 'Demo123!', phone: '', clinic_id: '', reason: '' });

  // Assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignPatient, setAssignPatient] = useState(null);
  const [assignClinicId, setAssignClinicId] = useState('');

  const loadPatients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/patients`);
      setPatients(res.data.patients);
      setFiltered(res.data.patients);
    } catch { toast.error('Failed to load patients'); }
    finally { setLoading(false); }
  };

  const loadClinics = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/clinics`);
      setClinics(res.data.clinics);
    } catch {}
  };

  useEffect(() => { loadPatients(); loadClinics(); }, []);

  useEffect(() => {
    if (!searchTerm) { setFiltered(patients); return; }
    const q = searchTerm.toLowerCase();
    setFiltered(patients.filter((p) =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.clinic_name || '').toLowerCase().includes(q)
    ));
  }, [searchTerm, patients]);

  const handleCreate = async () => {
    if (creating) return;
    const problem = passwordProblem(createForm.password);
    if (problem) { toast.error(problem); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/v1/superadmin/patients`, {
        ...createForm,
        clinic_id: createForm.clinic_id || null,
      });
      toast.success('Patient created');
      setCreateOpen(false);
      setCreateForm({ full_name: '', email: '', password: 'Demo123!', phone: '', clinic_id: '', reason: '' });
      loadPatients();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to create patient'));
    } finally {
      setCreating(false);
    }
  };

  const handleAssign = async () => {
    if (!assignPatient || !assignClinicId || assigning) return;
    setAssigning(true);
    try {
      await axios.put(`${API_URL}/api/v1/superadmin/patients/${assignPatient.id}/assign`, { clinic_id: assignClinicId });
      toast.success('Patient assigned to clinic');
      setAssignOpen(false);
      loadPatients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><PeopleIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>All Patients</Typography>
            <Typography variant="body2" color="text.secondary">Manage patients across all clinics</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Add Patient</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField fullWidth placeholder="Search by name, email, or clinic…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Clinic</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6}>No patients found.</TableCell></TableRow>
            ) : filtered.map((p) => (
              <TableRow key={p.id} hover sx={p.is_active === false ? { opacity: 0.6 } : undefined}>
                <TableCell><Typography fontWeight={600}>{p.full_name}</Typography></TableCell>
                <TableCell>{p.email || '—'}</TableCell>
                <TableCell>{p.clinic_name || <Chip size="small" label="Unassigned" color="warning" />}</TableCell>
                <TableCell>{p.provider_name || '—'}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={p.is_active === false ? 'Inactive' : 'Active'}
                    color={p.is_active === false ? 'default' : 'success'} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View History">
                    <IconButton size="small" onClick={() => navigate(`/patients/${p.id}/history`)}><HistoryIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Assign to Clinic">
                    <IconButton size="small" onClick={() => { setAssignPatient(p); setAssignClinicId(p.clinic_id || ''); setAssignOpen(true); }}>
                      <AssignIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Patient Dialog */}
      <Dialog open={createOpen} onClose={() => { if (!creating) setCreateOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Patient</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Full Name" value={createForm.full_name}
            onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} margin="normal" required />
          <TextField fullWidth label="Email" value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} margin="normal" required />
          <TextField fullWidth label="Password" type="password" value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} margin="normal" required
            helperText={PASSWORD_RULES_TEXT} />
          <TextField fullWidth label="Phone" value={createForm.phone}
            onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} margin="normal" />
          <TextField fullWidth label="Reason / Chief Complaint" value={createForm.reason}
            onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })} margin="normal" />
          <TextField fullWidth select label="Assign to Clinic" value={createForm.clinic_id}
            onChange={(e) => setCreateForm({ ...createForm, clinic_id: e.target.value })} margin="normal">
            <MenuItem value="">None</MenuItem>
            {clinics.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={creating || !createForm.full_name || !createForm.email}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Clinic Dialog */}
      <Dialog open={assignOpen} onClose={() => { if (!assigning) setAssignOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Patient to Clinic</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {assignPatient?.full_name}
          </Typography>
          <TextField fullWidth select label="Clinic" value={assignClinicId}
            onChange={(e) => setAssignClinicId(e.target.value)} margin="normal">
            {clinics.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(false)} disabled={assigning}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign}
            disabled={assigning || !assignClinicId}
            startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : null}>
            {assigning ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
